/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  MotifButton,
  MotifFormField,
  MotifErrorMessage,
} from "@ey-xd/motif-react";

import { useEffect, useId, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { AppDispatch, RootState } from "../../../store";
import "./MultiSelectUserSearch.scss";
import {
  resetSearchUsers,
  searchUsers,
} from "../../../store/slices/CTDBUserListSlice";
import { MotifIcon } from "@ey-xd/motif-react";
import {
  MotifNavigationIcClose24px,
  IconoirArrowUpTag,
  IconoirPlus,
} from "@ey-xd/motif-icon";
import SearchInput from "../../SearchInput/SearchInput";

interface ProxyOwner {
  Name: string;
  Email: string;
}

const MultiSelectUserSearch = ({
  proxyOwners,
  readOnly,
  setProxyOwners,
  resetSearch = false,
}: any) => {
  const [userSearch, setUserSearch] = useState<string[]>([]);
  const [userCurrentSearch, setUserCurrentSearch] = useState<string>("");
  const [searchCharCount, setSearchCharCount] = useState(0);
  const [proxyOwnersList, setProxyOwnersList] =
    useState<ProxyOwner[]>(proxyOwners);
  const [showProxyOwnerSearch, setShowProxyOwnerSearch] = useState(
    proxyOwners && proxyOwners.length > 0 ? false : true,
  );
  const [expandProxyOwnerSearch, setExpandProxyOwnerSearch] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  useEffect(() => {
    setProxyOwnersList(proxyOwners);
    setProxyOwners(proxyOwners);
    setShowProxyOwnerSearch(
      proxyOwners && proxyOwners.length > 0 ? false : true,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proxyOwners]);

  useEffect(() => {
    dispatch(resetSearchUsers());
  }, []);

  useEffect(() => {
    if (resetSearch) {
      setUserCurrentSearch("");
      setSearchCharCount(0);
    }
  }, [resetSearch]);

  const userList = useSelector(
    (state: RootState) => state.CTDBUserListSlice.data,
  );
  const userListLoading = useSelector(
    (state: RootState) => state.CTDBUserListSlice.isLoading,
  );
  const handleSearchInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (readOnly) return; // Prevent input change if readOnly is true
    const inputValue = event.target.value;

    setSearchCharCount(inputValue?.length);
    setUserCurrentSearch(inputValue);
    if (inputValue.trim().length >= 3) {
      dispatch(searchUsers({ searchText: inputValue.trim() }));
    }
  };

  const clearSearch = () => {
    setUserCurrentSearch("");
    setUserSearch([]);
    dispatch(resetSearchUsers());
  };
  const handleUserClick = (userData: string) => {
    if (readOnly) return;
    if (!userData) return;
    const newProxyOwner = {
      Name: userData.split("(")[0].trim(),
      Email: userData.split("(")[1].split(")")[0].trim(),
    };
    setProxyOwnersList([...proxyOwnersList, newProxyOwner]);
    setProxyOwners([...proxyOwnersList, newProxyOwner]);
    setUserSearch([...userSearch, userCurrentSearch]);
    setUserCurrentSearch("");
    dispatch(resetSearchUsers());
    setShowProxyOwnerSearch(false);
  };

  const handleDeleteClick = (event: any, email: string) => {
    if (readOnly) return; // Prevent input change if readOnly is true
    event.stopPropagation();
    if (proxyOwnersList.length === 1) {
      setShowProxyOwnerSearch(true);
    }
    const filetrEmailsList = proxyOwnersList.filter((user) => {
      return user.Email !== email;
    });
    setProxyOwners(filetrEmailsList);
    setProxyOwnersList(filetrEmailsList);
  };

  const handleSelectedUserGridClick = () => {
    if (readOnly) return; // Prevent input change if readOnly is true
    setShowProxyOwnerSearch(!showProxyOwnerSearch);
  };
  const filteredItems = Array.isArray(userList)
    ? userList.filter((userData: any) => {
        const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]*\.*(ey)\.com$/;
        return emailRegex.test(userData.email);
      })
    : [];

  const displayItems = filteredItems.map(
    (userData: any) => `${userData.displayName} (${userData.email})`,
  );
  return (
    <>
      <div className="commercial-proxy-owner-grid">
        {proxyOwnersList && proxyOwnersList.length > 0 && (
          <div
            className={`selected-users ${readOnly ? "disabled-selected-users" : ""}`}
            onClick={handleSelectedUserGridClick}
          >
            {proxyOwnersList.map((user, index) => {
              if (index > 1) {
                return (
                  <div
                    key={index}
                    className={`selected-user ${
                      expandProxyOwnerSearch ? "" : "hide"
                    }`}
                  >
                    {user.Name}
                    <span
                      className="remove-user"
                      onClick={(e) => handleDeleteClick(e, user.Email)}
                    >
                      <MotifIcon iconFunction={MotifNavigationIcClose24px} />
                    </span>
                  </div>
                );
              }
              return (
                <div key={index} className="selected-user">
                  {user.Name}
                  <span
                    className="remove-user"
                    onClick={(e) => handleDeleteClick(e, user.Email)}
                  >
                    <MotifIcon iconFunction={MotifNavigationIcClose24px} />
                  </span>
                </div>
              );
            })}
            {proxyOwnersList.length > 2 && (
              <div
                className="selected-user collapsed-grid"
                onClick={(event) => {
                  if (readOnly) return;
                  event.stopPropagation();
                  setExpandProxyOwnerSearch(!expandProxyOwnerSearch);
                }}
              >
                {expandProxyOwnerSearch ? (
                  <span>
                    <MotifIcon
                      size="24px"
                      className="collapse-btn"
                      iconFunction={IconoirArrowUpTag}
                    />
                  </span>
                ) : (
                  <MotifButton className="more-users">
                    <MotifIcon iconFunction={IconoirPlus} />
                    {Number(proxyOwnersList.length) - 2}
                  </MotifButton>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      <MotifFormField>
        <div className={!showProxyOwnerSearch ? "hide" : ""}>
          <SearchInput
            aria-label="Search for a user"
            id={`user-search-multiselect-${useId()}`}
            placeholder="Enter a user's name or email id"
            open={!userListLoading}
            className={`commercial-proxy-owner-search-input ${
              searchCharCount >= 3 ? "show-suggestions" : ""
            } ${readOnly ? "disabled-item" : ""}
            `}
            items={
              Array.isArray(userList)
                ? userList
                    .filter((userData: any) => {
                      if (
                        proxyOwnersList.find(
                          (proxyOwner) => proxyOwner.Email === userData.email,
                        )
                      ) {
                        return false;
                      }
                      const emailRegex =
                        /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]*\.*(ey)\.com$/;
                      return emailRegex.test(userData.email);
                    })
                    .map((userData: any) => {
                      return userData.displayName + " (" + userData.email + ")";
                    })
                : []
            }
            onChange={(e) => handleSearchInputChange(e)}
            onSelect={(item: any) => handleUserClick(item)}
            visibleOptions={3}
            value={userCurrentSearch}
            multiple={true}
            disabled={readOnly}
            clearSearch={clearSearch}
          />
          {userCurrentSearch.trim().length > 3 &&
            !userListLoading &&
            userList &&
            !displayItems.length && (
              <MotifErrorMessage
                aria-live="assertive"
                role="alert"
                id="aria-error-primaryList"
              >
                No Results Found
              </MotifErrorMessage>
            )}
        </div>
      </MotifFormField>
    </>
  );
};

export default MultiSelectUserSearch;
